import {
  Autocomplete,
  Box,
  FormControlLabel,
  List,
  Radio,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useEffect, useMemo } from 'react';

import { useDAL } from '@/contexts/useDAL';

import { FormTextField } from '../form/formTextField';
import { getCloudFieldConfig } from '../form/validation/cloudFieldValidation';
import { OptionListItem } from '../shared/optionListItem';

const arnConfig = getCloudFieldConfig(Provider.Aws, 'arn');

export const EncryptionComponent = ({
  encryption = {
    isEncrypted: true,
    encryptionKeyId: undefined,
    arn: undefined,
    mode: 'select',
  },
  accountId,
  onChange,
  regionName,
  canBeTurnedOff = false,
  title = 'Encrypt this resource',
  defaultOptionLabel,
}: {
  encryption?: Encryption;
  accountId: string | undefined;
  regionName: string | undefined;
  onChange: (encryption: Encryption) => void;
  canBeTurnedOff?: boolean;
  title?: string;
  defaultOptionLabel?: string;
}) => {
  const dal = useDAL();
  const { isLoading, data: body } = dal.restore.encryptionKeys.useList(
    accountId,
    regionName,
    false
  );

  const options = useMemo(() => {
    const keys = body?.keys || [];
    if (!defaultOptionLabel) {
      return keys;
    }
    return [
      { id: '__default__', arn: '', aliasName: defaultOptionLabel },
      ...keys,
    ];
  }, [body?.keys, defaultOptionLabel]);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    if (
      encryption.encryptionKeyId &&
      !body?.keys.find((key) => key.arn === encryption.encryptionKeyId)
    ) {
      onChange({ ...encryption, encryptionKeyId: undefined });
    }
  }, [body?.keys, encryption, encryption.encryptionKeyId, isLoading, onChange]);

  const setMode = (mode: 'arn' | 'select') => {
    onChange({ ...encryption, mode });
  };

  const isSelectMode = encryption.mode === 'select';
  const isArnMode = encryption.mode === 'arn';

  const selectedValue = useMemo(() => {
    if (!encryption.encryptionKeyId && defaultOptionLabel) {
      return options[0] || null;
    }
    return options.find((x) => x.arn === encryption.encryptionKeyId) || null;
  }, [encryption.encryptionKeyId, defaultOptionLabel, options]);

  return (
    <Box className='mx-[40px] my-[24px]'>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Typography>{title}</Typography>
        {canBeTurnedOff && (
          <Switch
            size='small'
            checked={encryption.isEncrypted}
            onChange={() => {
              const isEncrypted = !encryption.isEncrypted;
              onChange({
                ...encryption,
                isEncrypted,
              });
            }}
          />
        )}
      </Stack>
      {encryption.isEncrypted && (
        <List className='mt-[20px]'>
          <OptionListItem
            data-testid='encryptionSelectionMode'
            isChecked={isSelectMode}
            onClick={() => {
              setMode('select');
            }}
          >
            <Stack
              className='w-full'
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              gap='40px'
            >
              <FormControlLabel
                checked={isSelectMode}
                onClick={() => {
                  setMode('select');
                }}
                control={<Radio />}
                label='Select an existing key'
              />
              {isSelectMode && (
                <Autocomplete
                  sx={{ width: '50%' }}
                  data-testid='encryptionKeySelect'
                  clearOnBlur
                  disableClearable={!!defaultOptionLabel}
                  size='small'
                  value={selectedValue ?? null}
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      key={option.id}
                      data-testid={`encryptionKeyOption-${options.indexOf(option)}`}
                    >
                      {option?.aliasName}
                    </li>
                  )}
                  onChange={(event, newValue) => {
                    const keyId =
                      newValue?.arn === '' ? undefined : newValue?.arn;
                    const newState = {
                      ...encryption,
                      encryptionKeyId: keyId,
                    };
                    event.stopPropagation();
                    onChange(newState);
                  }}
                  getOptionLabel={(option) => option?.aliasName || ''}
                  isOptionEqualToValue={(option, value) =>
                    option?.arn === value?.arn
                  }
                  renderInput={(params) => {
                    return (
                      <TextField
                        {...params}
                        placeholder='Select'
                        label=''
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                    );
                  }}
                  options={options}
                />
              )}
            </Stack>
          </OptionListItem>
          <OptionListItem
            data-testid='encryptionTypeMode'
            isChecked={isArnMode}
            onClick={() => setMode('arn')}
          >
            <Stack
              className='w-full'
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              gap='40px'
            >
              <FormControlLabel
                checked={isArnMode}
                onClick={() => setMode('arn')}
                control={<Radio />}
                label='Enter a key ARN'
              />
              {isArnMode && (
                <FormTextField
                  title=''
                  dataTestId='encryptionKeyArn'
                  placeholder={arnConfig.placeholder}
                  value={encryption.arn || ''}
                  onChange={(event) => {
                    const newState = {
                      ...encryption,
                      arn: event.target.value,
                    };
                    onChange(newState);
                  }}
                  sx={{ width: '50%' }}
                  validation={arnConfig.validation}
                />
              )}
            </Stack>
          </OptionListItem>
        </List>
      )}
    </Box>
  );
};

export const getEncryptionKey = (
  encryption?: Encryption
): string | undefined => {
  if (!encryption?.isEncrypted) {
    return;
  }

  return encryption.mode === 'select'
    ? encryption.encryptionKeyId
    : encryption.arn;
};

export interface Encryption {
  isEncrypted: boolean;
  encryptionKeyId: string | undefined;
  arn: string | undefined;
  mode: 'arn' | 'select';
}
